import React, { useEffect, useState, useMemo } from 'react';
import shortid from 'shortid';
import { withRouter } from 'react-router';
import moment from 'moment';
import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';
import { useStore, useIsRole } from 'hooks';
import { groupBy } from 'helpers/utils';
import { ROLE, MONTHLY_FUND_STATUS, VALIDATION, CONTRACT_STATUS } from 'helpers/constants';
import { Button, ButtonClose } from 'components/ui/form';
import { Input, Select, DateInput } from 'components/ui/formik';
import { ContractService, ProductService } from 'services';
import styles from './contract.module.scss';

const ContractValidationSchema = Yup.object().shape({
  name: Yup.string()
    .max(...VALIDATION.MAX(255, VALIDATION.MAX_STRING))
    .required(...VALIDATION.REQUIRED),
  date: Yup.string()
    .typeError(...VALIDATION.TYPE)
    .required(...VALIDATION.REQUIRED),
  monthlyFund: Yup.string()
    .required(...VALIDATION.REQUIRED),
  status: Yup.string()
    .required(...VALIDATION.REQUIRED),
  debt: Yup.string()
    .required(...VALIDATION.REQUIRED),
  isPaymentDeferralProvided: Yup.string()
    .required(...VALIDATION.REQUIRED),
  paymentDeferralEndDate: Yup.string()
    .nullable()
    .when('isPaymentDeferralProvided', {
      is: value => Number(value) === 1,
      then: schema => schema
        .typeError(...VALIDATION.TYPE)
        .required(...VALIDATION.REQUIRED)
        .test(
          'not-in-past',
          'Дата окончания отсрочки должна быть не раньше текущей даты.',
          (value) => {
            if (!value) return false;
            const parsedStrict = moment(value, ['YYYY-MM-DD', moment.ISO_8601], true);
            const parsed = parsedStrict.isValid() ? parsedStrict : moment(new Date(value));
            return parsed.isValid() && parsed.isSameOrAfter(moment().startOf('day'), 'day');
          }
        ),
      otherwise: schema => schema.nullable(),
    }),
  balance: Yup.number()
    .required(...VALIDATION.REQUIRED),
  products: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.number()
          .typeError(...VALIDATION.PRODUCT)
          .required(...VALIDATION.REQUIRED),
        funds: Yup.array()
          .of(
            Yup.object().shape({
              total: Yup.number()
                .min(...VALIDATION.MIN(0, VALIDATION.MIN_NUMBER))
                .typeError(...VALIDATION.NUMBER)
                .required(...VALIDATION.REQUIRED),
            }),
          ),
      }),
    ),
});

const useFetch = (edit) => {
  const { setLoading, setUser } = useStore();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const productsResponse = await new ProductService({ setUser }).getProducts({ page: 0, limit: 1000 });
      const fetchedProducts = Array.isArray(productsResponse)
        ? productsResponse
        : (productsResponse?.items ?? []);
      const contractProducts = (edit?.contractProducts ?? [])
        .map(({ product }) => product)
        .filter(Boolean);

      const uniqueProductsById = [...contractProducts, ...fetchedProducts].reduce((acc, item) => {
        if (!acc.some(({ id }) => Number(id) === Number(item.id))) {
          acc.push(item);
        }
        return acc;
      }, []);

      setProducts(uniqueProductsById);
      setLoading(false);
    })();
  }, [setUser, setLoading, edit]);

  return [products];
};

const Contract = ({
  edit,
  onClose,
  match: {
    params: { userId },
  },
}) => {
  const { setUser, setLoading } = useStore();
  const canManageContracts = useIsRole([ROLE.ADMIN, ROLE.MANAGER]);
  const canEditContract = canManageContracts;

  const [products] = useFetch(edit);

  const getProductItem = (data = {}) => ({
    id: false,
    prevId: false,
    funds: new Array(12).fill(null).map(() => ({
      key: shortid.generate(),
      total: 0,
    })),
    ...data,
  });

  const hasItem = (hasId, excludeId, _products = []) => _products
    .filter(({ id }) => Number(id) !== Number(excludeId))
    .map(({ id }) => Number(id)).includes(Number(hasId));

  const initialValues = useMemo(() => ({
    name: edit ? edit.name : '',
    date: edit ? moment(edit.date).format('YYYY-MM-DD') : '',
    monthlyFund: edit ? Number(edit.monthlyFund) : 0,
    status: edit ? edit.status : CONTRACT_STATUS.performing,
    balance: edit ? edit.balance : 0,
    debt: edit ? Number(edit.debt) : 0,
    isPaymentDeferralProvided: edit ? Number(edit.isPaymentDeferralProvided) : 0,
    paymentDeferralEndDate: edit?.paymentDeferralEndDate
      ? moment(edit.paymentDeferralEndDate).format('YYYY-MM-DD')
      : '',
    products: edit
      ? edit.contractProducts.map(({ productId, funds = [] }) => {
        const g = groupBy(funds, 'month');
        return getProductItem({
          isMayBeRemoved: false,
          key: shortid.generate(),
          id: productId,
          prevId: productId,
          funds: new Array(12).fill(null).reduce((prev, next, month) => {
            const total = (month in g && Array.isArray(g[month]) && g[month].length)
              ? g[month][0].total
              : 0;

            prev.push({
              key: shortid.generate(),
              total,
            });

            return prev;
          }, []),
        });
      })
      : new Array(0).fill(null).map(() => getProductItem({
        key: shortid.generate(),
      })),
  }), [edit]);

  const onSubmit = async (values) => {
    if (!canEditContract) {
      onClose(false);
      return;
    }

    const contractService = new ContractService({ setUser });
    setLoading(true);

    const prepareValues = Object.keys(values).reduce((prev, key) => {
      const value = values[key];

      switch (key) {
        case 'status':
          prev[key] = Number(value);
          break;
        case 'debt':
        case 'monthlyFund':
        case 'isPaymentDeferralProvided':
          prev[key] = Boolean(Number(value));
          break;
        case 'date':
          prev[key] = new Date(value);
          break;
        case 'paymentDeferralEndDate':
          prev[key] = value ? moment(value).format('YYYY-MM-DD') : null;
          break;
        case 'products':
          prev[key] = value.map(({ id, prevId, funds }) => ({
            id: Number(id),
            prevId: typeof prevId === 'boolean' ? prevId : Number(prevId),
            funds: funds.map(({ total }) => Number(total)),
          }));
          break;
        default:
          prev[key] = values[key];
          break;
      }

      return prev;
    }, {});

    if (!prepareValues.isPaymentDeferralProvided) {
      prepareValues.paymentDeferralEndDate = null;
    }

    if (edit) {
      await contractService.updateContract({ contractId: edit.id, ...prepareValues });
    } else {
      await contractService.createContract({ userId, ...prepareValues });
    }

    setLoading(false);
    onClose(true);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={ContractValidationSchema}
      onSubmit={onSubmit}
    >
      {({ values, handleSubmit, setFieldValue }) => (
        <form onSubmit={handleSubmit} className={styles.contract} autoComplete="off">
          <div className={styles.main}>
            <div className={styles.title}>
              {canManageContracts ? (
                `${edit ? 'Редактирование' : 'Добавление'} контракта`
              ) : 'Контракт'}
            </div>
            <div className={styles.fields}>
              <div className={styles.field}>
                <Input disabled={!canEditContract} label="Номер договора" placeholder="Номер договора" name="name" />
              </div>
              <div className={styles.field}>
                <DateInput disabled={!canEditContract} label="Дата договора" placeholder="Дата" name="date" />
              </div>
              <div className={styles.field}>
                <Select disabled={!canEditContract} label="Месячный фонд" name="monthlyFund">
                  {[MONTHLY_FUND_STATUS.MONTH, MONTHLY_FUND_STATUS.SUMMA].map(value => (
                    <option value={value} key={value}>
                      {MONTHLY_FUND_STATUS.toString(value)}
                    </option>
                  ))}
                </Select>
              </div>
              <div className={styles.field}>
                <Select disabled={!canEditContract} label="Статус" placeholder="Статус" name="status">
                  {CONTRACT_STATUS.list().map(status => (
                    <option key={status} value={status}>
                      {CONTRACT_STATUS.toString(status)}
                    </option>
                  ))}
                </Select>
              </div>
              <div className={styles.field}>
                <Input
                  disabled={!canEditContract}
                  label="Баланс"
                  placeholder="Баланс"
                  name="balance"
                  type="number"
                />
              </div>
              <div className={styles.field}>
                <Select disabled={!canEditContract} label="Задолженность" placeholder="Задолженность" name="debt">
                  <option value={0}>Нет</option>
                  <option value={1}>Да</option>
                </Select>
              </div>
              <div className={styles.field}>
                <Select
                  disabled={!canEditContract}
                  label="Предоставляем отсрочку платежа"
                  name="isPaymentDeferralProvided"
                  onChange={({ target: { value } }) => {
                    if (Number(value) !== 1) {
                      setFieldValue('paymentDeferralEndDate', '');
                    }
                  }}
                >
                  <option value={0}>Нет</option>
                  <option value={1}>Да</option>
                </Select>
              </div>
              {Number(values.isPaymentDeferralProvided) === 1 ? (
                <div className={styles.field}>
                  <DateInput
                    disabled={!canEditContract}
                    label="Дата окончания отсрочки"
                    placeholder="Дата"
                    name="paymentDeferralEndDate"
                  />
                </div>
              ) : null}
            </div>
            <div className={styles.title}>Продукты</div>
            <div className={styles.fields}>
              <FieldArray name="products">
                {({ remove, push }) => (
                  <>
                    <div className={styles.products}>
                      {values.products.map(({ isMayBeRemoved = true, funds, key, id }, pIndex) => (
                        <div key={key} className={styles.product}>
                          <div className={styles.name}>
                            <div className={styles.field}>
                              <Select disabled={!canEditContract} label="Продукт" name={`products[${pIndex}].id`}>
                                <option key="default" value={false}>Выбрать</option>
                                {(Array.isArray(products) ? products : []).map(({ id: _id, name }) => (
                                  hasItem(_id, id, values.products)
                                    ? null
                                    : (
                                      <option key={_id} value={_id}>
                                        {name}
                                      </option>
                                    )
                                ))}
                              </Select>
                            </div>
                          </div>
                          <div className={styles.funds}>
                            {funds.map(({ key: keyFund }, fINdex) => {
                              const fundIndexIncrement = fINdex + 1;
                              const monthIndex = fundIndexIncrement > 9 ? fundIndexIncrement : `0${fundIndexIncrement}`;
                              return (
                                <div key={keyFund} className={styles.fund}>
                                  <div className={styles.field}>
                                    <Input
                                      disabled={!canEditContract}
                                      label={`Фонд за ${monthIndex}`}
                                      placeholder="0"
                                      min={0}
                                      type="number"
                                      name={`products[${pIndex}].funds[${fINdex}].total`}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {canEditContract && isMayBeRemoved && (
                            <div className={styles.remove}>
                              <Button color="lightgray" onClick={() => remove(pIndex)}>Удалить</Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {canEditContract ? (
                      <div className={styles.add}>
                        <Button
                          onClick={() => push(getProductItem({ key: shortid.generate() }))}
                        >
                          Добавить продукт
                        </Button>
                      </div>
                    ) : null}
                  </>
                )}
              </FieldArray>
            </div>
          </div>
          <div className={styles.buttons}>
            {canEditContract ? (
              <>
                <div>
                  <Button type="submit">{edit ? 'Редактировать контракт' : 'Создать контракт'}</Button>
                </div>
                <div>
                  <ButtonClose onClick={() => onClose(false)}>{edit ? 'Закрыть' : 'Отменить'}</ButtonClose>
                </div>
              </>
            ) : (
              <>
                <div />
                <div>
                  <ButtonClose onClick={() => onClose(false)}>Закрыть</ButtonClose>
                </div>
              </>
            )}
          </div>
        </form>
      )}
    </Formik>
  );
};

export default withRouter(Contract);
